import { Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues } from '@card-software/library';
import { KnittingOrder, KnittingOrderSearchCommand } from '@app/models/production/knitting-order';
import { EnumService } from '@app/services/enum.service';
import {
  KnittingOrderBalanceView,
  KnittingOrderReportItem,
  LotItemBalanceView,
  LotItemYarnBalance,
  Requisition,
  RequisitionBalanceView,
} from '../../../models/knitting/kntiting-requistion-report';
import { KnittingOrderProxyService } from '../../../proxies/production/knitting-order.proxy.service';

@Component({
  selector: 'app-knitting-order-balance',
  templateUrl: './knitting-order-balance.component.html',
  styleUrl: './knitting-order-balance.component.scss',
})
export class KnittingOrderBalanceComponent {
  private translateService = inject(TranslateService);
  private knittingOrderService = inject(KnittingOrderProxyService);
  private enumService = inject(EnumService);

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  knittingOrderBalances = signal<KnittingOrderBalanceView[]>([]);
  knittingOrders = signal<KnittingOrder[]>([]);
  expandedRows: Record<string, boolean> = {};
  readonly filterState = createDynamicFilterState({ storageKey: 'knitting-order-balance-filters', config: this.filterConfig });
  showFilters = signal(false);

  statusOptions: EnumValues = {};
  private filtersPanelInitialized = false;

  // Pagination
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(50);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  private currentSearchCriteria: KnittingOrderSearchCommand = {};

  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.tryInitializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    this.statusOptions = this.enumService.getEnum('KnittingOrderStatus', 'productionEnums')?.values ?? {};
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    if (!this.statusOptions || Object.keys(this.statusOptions).length === 0) {
      this.statusOptions = this.enumService.getEnum('KnittingOrderStatus', 'productionEnums')?.values ?? {};
    }

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'knittingOrderNumber',
          label: translate('Order_Number', 'Order Number'),
          placeholder: translate('Enter_order_number', 'Enter order number'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Status', 'Status'),
          placeholder: translate('Select_Status', 'Select Status'),
          options: this.statusOptions,
          showClear: true,
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getStatuses = (value: any): number[] | undefined => {
      if (!value) return undefined;
      if (Array.isArray(value)) {
        if (value.length > 0 && typeof value[0] === 'object' && 'value' in value[0]) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return value.map((item: any) => item.value as number);
        }
        return value as number[];
      }
      return undefined;
    };

    const searchCriteria: KnittingOrderSearchCommand = {
      knittingOrderNumber: (filters['knittingOrderNumber'] as string) || undefined,
      statuses: getStatuses(filters['statuses']),
    };

    this.currentSearchCriteria = searchCriteria;
    this.currentPage.set(1);
    this.loadKnittingOrderBalances(searchCriteria);
  }

  loadKnittingOrderBalances(criteria?: KnittingOrderSearchCommand): void {
    this.startLoading();

    const searchCriteria = criteria || {};

    this.knittingOrderService
      .searchKnittingOrder(searchCriteria, this.currentPage(), this.pageSize(), false, this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: async (knittingOrdersResponse) => {
          if (knittingOrdersResponse?.success && knittingOrdersResponse.data) {
            const knittingOrders = knittingOrdersResponse.data;
            this.knittingOrders.set(knittingOrders.items);
            this.totalRecords.set(knittingOrders.totalCount);
            const knittingOrderIds = knittingOrders.items.map((ko) => ko.id!);

            if (knittingOrderIds.length > 0) {
              try {
                this.startLoading();
                const requisitionResponse = await this.knittingOrderService.getYarnBalanceByKnittingOrderIds(knittingOrderIds).toPromise();

                if (requisitionResponse?.success && requisitionResponse.data) {
                  this.knittingOrderBalances.set(this.transformToBalanceView(requisitionResponse.data));
                } else {
                  this.knittingOrderBalances.set([]);
                }
                this.stopLoading();
              } catch (error) {
                console.error('Error loading yarn balances:', error);
                this.knittingOrderBalances.set([]);
                this.stopLoading();
              }
            } else {
              this.knittingOrderBalances.set([]);
            }
          } else {
            this.knittingOrders.set([]);
            this.knittingOrderBalances.set([]);
            this.totalRecords.set(0);
          }
        },
        error: (error) => {
          console.error('Error loading knitting order balances:', error);
          this.knittingOrders.set([]);
          this.knittingOrderBalances.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.loadKnittingOrderBalances(this.currentSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = event.field;
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.loadKnittingOrderBalances(this.currentSearchCriteria);
  }

  private transformToBalanceView(reportItems: KnittingOrderReportItem[]): KnittingOrderBalanceView[] {
    return reportItems.map((item) => {
      const requisitions = this.transformRequisitions(item.requisitions);
      const returnedLotItems = this.transformReturnedLotItems(item.returnedLotItems || []);

      const totalOriginalQuantity = requisitions.reduce((sum, req) => sum + req.totalOriginalQuantity, 0);
      const totalCurrentQuantity = requisitions.reduce((sum, req) => sum + req.totalCurrentQuantity, 0);
      const totalReturnedQuantity = returnedLotItems.reduce((sum, item) => sum + item.originalQuantity, 0);
      const totalConsumedQuantity = totalOriginalQuantity - totalCurrentQuantity;
      const totalLotItems = requisitions.reduce((sum, req) => sum + req.lotItems.length, 0);
      const knittingOrder = this.knittingOrders().find((ko) => ko.id === item.knittingOrderId);

      return {
        knittingOrderId: item.knittingOrderId,
        knittingOrderNumber: item.knittingOrderNumber,
        workOrderNumber: item.workOrderNumber,
        status: knittingOrder?.status ?? 0,
        requisitions,
        returnedLotItems,
        totalLotItems,
        totalOriginalQuantity,
        totalCurrentQuantity,
        totalReturnedQuantity,
        totalConsumedQuantity,
        issuedQuantity: knittingOrder?.issuedQuantity ?? 0,
        plannedQuantity: this.getPlannedQuantity(knittingOrder!) ?? 0,
        manufacturerLotNumbers: this.getDistinctLotNumbers(knittingOrder!) ?? '',
      };
    });
  }

  getPlannedQuantity(ko: KnittingOrder): number {
    return ko.fabricQuantityDetails.reduce((sum, detail) => sum + (detail.quantity || 0), 0);
  }

  getDistinctLotNumbers(ko: KnittingOrder): string {
    const lotNumbers = new Set<string>();
    ko.fabricQuantityDetails.forEach((detail) => {
      detail.yarnAndProviderInfos.forEach((info) => {
        if (info.providerLotNumber) {
          lotNumbers.add(info.providerLotNumber);
        }
      });
    });
    return Array.from(lotNumbers).join(', ');
  }

  private transformRequisitions(requisitions: Requisition[]): RequisitionBalanceView[] {
    return requisitions.map((req) => {
      const allLotItems: LotItemYarnBalance[] = [];
      req.inventoryTransactions.forEach((transaction) => {
        allLotItems.push(...transaction.lotItems);
      });

      const lotItems = allLotItems.map((lot) => ({
        lotItemNumber: lot.lotItemNumber,
        itemCode: lot.itemCode,
        manufacturerLotNumber: lot.providerLotNumber,
        itemDescription: lot.itemDescription,
        originalQuantity: lot.orgQuantity,
        currentQuantity: lot.currentQuantity,
        consumedQuantity: lot.orgQuantity - lot.currentQuantity,
        unitOfMeasure: lot.unitOfMeasure,
        providerLotNumber: lot.providerLotNumber,
      }));

      const totalOriginalQuantity = lotItems.reduce((sum, lot) => sum + lot.originalQuantity, 0);
      const totalCurrentQuantity = lotItems.reduce((sum, lot) => sum + lot.currentQuantity, 0);
      const totalConsumedQuantity = totalOriginalQuantity - totalCurrentQuantity;

      return {
        requisitionNumber: req.requisitionNumber,
        lotItems,
        totalOriginalQuantity,
        totalCurrentQuantity,
        totalConsumedQuantity,
      };
    });
  }

  private transformReturnedLotItems(returnedLotItems: LotItemYarnBalance[]): LotItemBalanceView[] {
    return returnedLotItems.map((lot) => ({
      lotItemNumber: lot.lotItemNumber,
      itemCode: lot.itemCode,
      manufacturerLotNumber: lot.providerLotNumber,
      itemDescription: lot.itemDescription,
      originalQuantity: lot.orgQuantity,
      currentQuantity: lot.currentQuantity,
      consumedQuantity: 0,
      unitOfMeasure: lot.unitOfMeasure,
      providerLotNumber: lot.providerLotNumber,
    }));
  }

  getConsumptionPercentage(original: number, current: number): number {
    if (original === 0) return 0;
    return ((original - current) / original) * 100;
  }

  getTotalLotItems(): number {
    return this.knittingOrderBalances().reduce((sum, order) => sum + order.totalLotItems, 0);
  }

  getTotalOriginalQuantity(): number {
    return this.knittingOrderBalances().reduce((sum, order) => sum + order.totalOriginalQuantity, 0);
  }

  getTotalConsumedQuantity(): number {
    return this.knittingOrderBalances().reduce((sum, order) => sum + order.totalConsumedQuantity, 0);
  }

  getTotalReturnedQuantity(): number {
    return this.knittingOrderBalances().reduce((sum, order) => {
      const returnedQty = order.returnedLotItems.reduce((itemSum, item) => itemSum + item.originalQuantity, 0);
      return sum + returnedQty;
    }, 0);
  }

  getReturnedQuantityForOrder(order: KnittingOrderBalanceView): number {
    return order.returnedLotItems.reduce((sum, item) => sum + item.originalQuantity, 0);
  }
}
